import type { ApiError } from "../types";
import { apiProxyUrl } from "./service-endpoints";
import { mcpCall as callUiTool } from "./mcp";
import { triggerReloginForResponse } from "./auth-relogin.ts";
import i18n from "./i18n";

/**
 * Dashboard je presentation client. Nástroje volá přes samostatnou MCP URL;
 * Python proxy pod browser session znovu rozřeší entitu a ACL.
 *
 *  - MCP tooly:    POST /mcp  (JSON-RPC 2.0)
 *  - REST tunel:   ANY  /proxy/local{path}  — python → Deno s identitou
 *
 * Browser nikdy nedrží ACM API klíč. Historické REST helpery níže jsou pouze
 * migrační dluh pro binární/stream capability; nepoužívej je pro nové JSON řízení.
 */

// Credentials nikdy nepatří do browser storage. Odstraň i pozůstatky starších
// dashboard buildů, aby je nemohl přečíst ani neprovedený legacy request.
if (typeof window !== "undefined") {
  for (const key of ["acm_api_key", "acm-api-key", "acm_key"]) {
    window.localStorage.removeItem(key);
  }
}

/** Převod původní path (`/apiext/...`, `/manage/...`, `/api/...`) na proxy path. */
export class ApiClientError extends Error {
  status: number;
  data?: ApiError;

  constructor(message: string, status: number, data?: ApiError) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.data = data;
  }
}

async function req<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const controller = new AbortController();
  // 120s — scan sítě, dlouhé MCP nástroje. Větší akce (deploy) mají vlastní cestu.
  const timeout = setTimeout(() => controller.abort(), 120000);

  try {
    const hasBody = body !== undefined && body !== null;
    // Presentation boundary exception: legacy binární/stream tunel (viz
    // hlavička souboru). apiProxyUrl() vždy resolvuje na tu samou MCP proxy
    // (lib/service-endpoints.ts) — UI si tu cestu nikdy neskládá samo.
    const r = await fetch(apiProxyUrl(path), {
      method,
      credentials: "include",
      headers: hasBody ? { "Content-Type": "application/json" } : undefined,
      body: hasBody ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeout);

    if (triggerReloginForResponse(r)) {
      throw new ApiClientError(
        i18n.t("auth.sessionExpired", "Přihlášení vypršelo; obnovuji autorizaci."),
        401,
      );
    }

    if (!r.ok) {
      const err = await r.json().catch(() => ({
        error: r.statusText,
      })) as ApiError;
      throw new ApiClientError(
        err.error || `HTTP ${r.status}: ${r.statusText}`,
        r.status,
        err,
      );
    }

    return r.json() as Promise<T>;
  } catch (e) {
    clearTimeout(timeout);

    if (e instanceof ApiClientError) {
      throw e;
    }

    if (e instanceof Error && e.name === "AbortError") {
      throw new ApiClientError(
        i18n.t("api.timeout", "Request timeout - server neodpovídá"),
        408,
      );
    }

    throw new ApiClientError(
      e instanceof Error ? e.message : i18n.t("errors.unknownError", "Neznámá chyba"),
      500,
    );
  }
}

/** MCP JSON-RPC tool call přes Python proxy.
 *  Loading UX: dlouhé operace (>800ms) triggerne loading toast, failure vždy
 *  error toast — přes `withOperationFeedback`. Silent: `{ silent: true }`.
 *  Custom label: `{ label: 'Mažu instanci' }`. */
async function mcpCall<T = unknown>(
  toolName: string,
  args: Record<string, unknown> = {},
  opts: { label?: string; silent?: boolean; hostLocal?: boolean } = {},
): Promise<T> {
  return await callUiTool<T>(toolName, args, opts);
}

async function mcpList(): Promise<
  Array<{ name: string; description?: string }>
> {
  // Enumerace toolů není UI capability. UI má znát jen nástroje, které její
  // konkrétní obrazovka potřebuje; agenti používají samostatný MCP listener.
  return [];
}

/** Compatibility no-op: browser-side ACM credentials are forbidden. */
export function setApiKey(_key: string | null) {}
export function getApiKey(): string {
  return "";
}

export const api = {
  get: <T>(path: string) => req<T>("GET", path),
  post: <T>(path: string, body?: unknown) => req<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => req<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => req<T>("PATCH", path, body),
  delete: <T>(path: string) => req<T>("DELETE", path),
  /** MCP tool call přes Python proxy */
  mcp: mcpCall,
  /** MCP tool call přes přihlášenou browser session. */
  mcpSession: <T = unknown>(
    toolName: string,
    args: Record<string, unknown> = {},
    opts: { label?: string; silent?: boolean; hostLocal?: boolean } = {},
  ) => mcpCall<T>(toolName, args, opts),
  /** UI neenumeruje MCP nástroje; tato compatibility metoda vrací prázdný seznam. */
  mcpList,
};

// Typed API helpers — wiki JSON ops i upload/download jdou přes MCP (žádný REST z UI).
export const wikiApi = {
  list: async (module?: string) => {
    const r = await api.mcp<{ ok?: boolean; entries?: import("../types").WikiEntry[] }>(
      "wiki.list",
      module ? { module } : {},
    );
    return r.entries ?? [];
  },
  get: async (id: string) => {
    const r = await api.mcp<{ ok?: boolean; entry?: import("../types").WikiEntry }>(
      "wiki.get",
      { id },
    );
    if (!r.entry) throw new ApiClientError(`Wiki entry '${id}' not found`, 404);
    return r.entry;
  },
  update: (id: string, body: Partial<import("../types").WikiEntry> & Record<string, unknown>) =>
    api.mcp<{ ok: boolean; id: string }>("wiki.update", { id, ...body }),
  delete: (id: string) =>
    api.mcp<{ ok: boolean; id: string }>("wiki.delete", { id }),
  /** Vrátí obsah záznamu jako base64 soubor (přes MCP), UI si sama udělá Blob download. */
  download: (id: string) =>
    api.mcp<{ ok: boolean; filename?: string; content_type?: string; base64?: string; error?: string }>(
      "wiki.download",
      { id },
    ),
  upload: async (
    file: File,
    opts?: { id?: string; title?: string; module?: string; tags?: string },
  ) => {
    const buf = await file.arrayBuffer();
    let bin = "";
    const bytes = new Uint8Array(buf);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      bin += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    const content_base64 = btoa(bin);
    const r = await api.mcp<
      {
        ok: boolean;
        id: string;
        title: string;
        size: number;
        lines: number;
        url: string;
        error?: string;
      }
    >(
      "wiki.upload",
      {
        filename: file.name,
        content_base64,
        id: opts?.id,
        title: opts?.title,
        module: opts?.module,
        tags: opts?.tags,
      },
    );
    if (!r.ok) {
      throw new ApiClientError(r.error ?? i18n.t("api.uploadFailed", "Nahrání selhalo"), 400);
    }
    return r;
  },
};

